// src/services/pftUploadPlan.js
//
// Firebase-free rules for the admin PFT upload page: which picked files go up,
// where they go, and why any are left out. Kept separate so it can be tested
// without loading Firebase (see pftUploadPlan.test.js).

// Matches the storage rule (storage.pft.rules): PDFs only, 0 < size < 50 MB.
export const MAX_FILE_BYTES = 50 * 1024 * 1024;

// How many files one upload session accepts. Keeps a session short (minutes, not
// hours) so a closed tab or sleeping laptop costs little; the next session skips
// whatever already went up.
export const MAX_FILES_PER_SESSION = 500;

// How many files upload at the same time.
export const PARALLEL_UPLOADS = 4;

// Every upload goes into one fixed folder per SITE: uploads/<tenantId>/<folder>/.
// Admins never see or choose it; it follows the site they're switched to. The
// storage rule is create-only, so a file name that is already there is refused,
// from any computer, at any time.
//
// Dubois's folder is "pft": it was the company-wide folder before uploads were
// per site, and every file in it (the ~5,000-report backlog) is a Dubois PFT.
// Keeping that name means none of them move. Every other site uses its own siteId.
// The extraction job maps folders back to sites the same way.
export const UPLOAD_FOLDER = "pft";
export const DUBOIS_UPLOAD = { tenantId: "lung-innovations", siteId: "lin-dubois1" };

export const folderForSite = (tenantId, siteId) =>
  tenantId === DUBOIS_UPLOAD.tenantId && siteId === DUBOIS_UPLOAD.siteId
    ? UPLOAD_FOLDER
    : String(siteId || "");

/**
 * The file's name inside the bucket. Keeps the original name (the job reports by
 * it) but removes anything that would act as a folder separator or a control
 * character.
 */
// "/" and "\" would act as folders; "#", "[", "]", "*" and "?" are characters
// Google Cloud Storage advises against in object names. They are percent-ENCODED,
// not replaced: replacing ("#" -> "_") made "synth#1.pdf" and "synth_1.pdf" the
// same name, so a different file was skipped as a duplicate. Encoding is
// reversible, so two different names can never collide. "%" itself is encoded
// too, so an original name that already contains "%23" can't collide either.
const ENCODE = { "%": "%25", "/": "%2F", "\\": "%5C", "#": "%23", "[": "%5B", "]": "%5D", "*": "%2A", "?": "%3F" };

export const objectFileName = (name) =>
  String(name || "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .trim()
    .replace(/[%/\\#[\]*?]/g, (c) => ENCODE[c]);

export const objectPath = (tenantId, batchId, fileName) =>
  `uploads/${tenantId}/${batchId}/${objectFileName(fileName)}`;

const isPdf = (file) =>
  file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");

/**
 * Sort a picked selection into what to upload and what to leave out.
 *   alreadyUploaded: Set of object file names already uploaded from this browser.
 * Returns { toUpload, skipped: [{name, reason}] }. Never throws.
 */
export const planUpload = (files, alreadyUploaded = new Set()) => {
  const toUpload = [];
  const skipped = [];
  const seen = new Set();
  for (const file of Array.from(files || [])) {
    const name = objectFileName(file.name);
    if (!isPdf(file)) skipped.push({ name, reason: "not a PDF" });
    else if (!file.size) skipped.push({ name, reason: "empty file" });
    else if (file.size >= MAX_FILE_BYTES) skipped.push({ name, reason: "larger than 50 MB" });
    else if (alreadyUploaded.has(name)) skipped.push({ name, reason: "already uploaded" });
    else if (seen.has(name)) skipped.push({ name, reason: "same file name picked twice" });
    else {
      seen.add(name);
      toUpload.push(file);
    }
  }
  if (toUpload.length > MAX_FILES_PER_SESSION) {
    for (const file of toUpload.splice(MAX_FILES_PER_SESSION)) {
      skipped.push({ name: objectFileName(file.name), reason: `over ${MAX_FILES_PER_SESSION} files: upload in the next session` });
    }
  }
  return { toUpload, skipped };
};

/** "1.2 GB" / "340 MB" / "12 KB" */
export const formatBytes = (n) => {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${Math.round(n / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};

/** The original file name, for display, from a stored (encoded) name. */
export const displayName = (stored) => {
  try {
    return decodeURIComponent(String(stored || ""));
  } catch {
    return String(stored || "");
  }
};
